import type { Dispatch, ReactElement, SetStateAction } from 'react';
import { Button, Col, Divider, Empty, Form, Input, Row, Select, Space, Typography } from 'antd';
import { DeleteOutlined, PlusOutlined } from '@ant-design/icons';
import type { ImageInfo } from '@app-types/commons/images.types.ts';
import ImageManager from '@tools/components/ImageManager.tsx';
import PhoneInput from '@tools/components/PhoneInput';
import type { CountryCode, E164Number } from 'libphonenumber-js';
import { isValidPhoneNumber } from 'react-phone-number-input';

import type { SocialNetwork } from '@tools/socialLinks';

type CustomerSocialNetwork = Exclude<SocialNetwork, 'twitter'>;

const { TextArea } = Input;

export type CustomerDocumentType = 'dni' | 'cuit' | 'passport' | 'other';

export interface CustomerSecondaryPhoneDraft {
  id?: number;
  label: string;
  number: E164Number | '';
  country: CountryCode;
}

export interface CustomerAddressDraft {
  id?: number;
  label: string;
  street: string;
  city: string;
  state: string;
  postcode: string;
  country: string;
  notes: string;
  phoneNumber: string;
}

export interface CustomerSocialDraft {
  facebook: string;
  instagram: string;
  tiktok: string;
  youtube: string;
  whatsapp: string;
  website: string;
}

export interface CustomerExtendedFormErrors {
  documentNumber?: string | null;
  social?: Partial<Record<CustomerSocialNetwork | 'whatsapp' | 'website', string | null>>;
  addresses?: Array<{ street?: string | null; city?: string | null } | null>;
  secondaryPhones?: Array<{ number?: string | null } | null>;
}

export interface CustomerExtendedFormValue {
  lastName: string;
  documentType: CustomerDocumentType;
  documentNumber: string;
  notes: string;
  avatarUrl: string;
  social: CustomerSocialDraft;
  addresses: CustomerAddressDraft[];
  secondaryPhones: CustomerSecondaryPhoneDraft[];
}

interface CustomerExtendedFormProps<TValue extends CustomerExtendedFormValue> {
  value: TValue;
  onChange: Dispatch<SetStateAction<TValue>>;
  errors?: CustomerExtendedFormErrors | null;
  disabled?: boolean;
  defaultPhoneCountry: CountryCode;
}

const DOCUMENT_TYPE_OPTIONS: Array<{ label: string; value: CustomerDocumentType }> = [
  { label: 'DNI', value: 'dni' },
  { label: 'CUIT/CUIL', value: 'cuit' },
  { label: 'Pasaporte', value: 'passport' },
  { label: 'Otro', value: 'other' },
];

const buildEmptyAddress = (country: CountryCode): CustomerAddressDraft => ({
  id: undefined,
  label: '',
  street: '',
  city: '',
  state: '',
  postcode: '',
  country,
  notes: '',
  phoneNumber: '',
});

const buildEmptySecondaryPhone = (country: CountryCode): CustomerSecondaryPhoneDraft => ({
  id: undefined,
  label: '',
  number: '',
  country,
});

const buildAvatarSelection = (avatarUrl: string): ImageInfo[] => {
  const trimmedAvatarUrl = avatarUrl.trim();
  if (!trimmedAvatarUrl) {
    return [];
  }

  return [
    {
      file_path_large: trimmedAvatarUrl,
      caption: 'Avatar del cliente',
    },
  ];
};

/**
 * Renderiza los campos avanzados del cliente y reutiliza el gestor de media
 * compartido para seleccionar el avatar sin duplicar lógica local.
 */
export function CustomerExtendedForm<TValue extends CustomerExtendedFormValue>({
  value,
  onChange,
  errors,
  disabled = false,
  defaultPhoneCountry,
}: CustomerExtendedFormProps<TValue>): ReactElement {
  const secondaryPhoneErrors = errors?.secondaryPhones ?? [];
  const addressErrors = errors?.addresses ?? [];
  const socialErrors = errors?.social ?? {};

  return (
    <Space orientation="vertical" style={{ width: '100%' }} size="large">
      <Space orientation="vertical" style={{ width: '100%' }}>
        <Form.Item label="Apellido" htmlFor="customerLastName">
          <Input
            id="customerLastName"
            placeholder="Apellido"
            value={value.lastName}
            disabled={disabled}
            maxLength={80}
            onChange={(event) =>
              onChange((prev) => ({ ...prev, lastName: event.target.value }))
            }
          />
        </Form.Item>

        <Space orientation="vertical" style={{ width: '100%' }}>
          <Typography.Text type="secondary">Documento</Typography.Text>
          <Space style={{ width: '100%' }} direction="vertical" size="small">
            <Select<CustomerDocumentType>
              value={value.documentType}
              disabled={disabled}
              onChange={(next) =>
                onChange((prev) => ({ ...prev, documentType: next }))
              }
              options={DOCUMENT_TYPE_OPTIONS}
              popupMatchSelectWidth={320}
            />
            <Form.Item
              htmlFor="customerDocumentNumber"
              help={errors?.documentNumber ?? undefined}
              validateStatus={errors?.documentNumber ? 'error' : undefined}
            >
              <Input
                id="customerDocumentNumber"
                placeholder="Número"
                value={value.documentNumber}
                maxLength={32}
                disabled={disabled}
                onChange={(event) =>
                  onChange((prev) => ({ ...prev, documentNumber: event.target.value }))
                }
              />
            </Form.Item>
          </Space>
        </Space>
      </Space>

      <Divider plain>Teléfonos secundarios</Divider>
      <Space orientation="vertical" style={{ width: '100%' }} size="middle">
        {value.secondaryPhones.length === 0 ? (
          <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Sin teléfonos secundarios" />
        ) : null}
        {value.secondaryPhones.map((phone, index) => {
          const phoneError = secondaryPhoneErrors[index]?.number ?? null;
          return (
            <Space
              key={phone.id ?? `secondary-phone-${index}`}
              direction="vertical"
              style={{ width: '100%', border: '1px solid var(--ant-color-border)', borderRadius: 8, padding: 12 }}
              size="small"
            >
              <Space style={{ width: '100%' }} align="start">
                <Form.Item style={{ flex: 1 }} label="Etiqueta" htmlFor={`customerSecondaryPhoneLabel-${index}`}>
                  <Input
                    id={`customerSecondaryPhoneLabel-${index}`}
                    placeholder="Ej: Trabajo"
                    value={phone.label}
                    disabled={disabled}
                    maxLength={40}
                    onChange={(event) =>
                      onChange((prev) => {
                        const next = [...prev.secondaryPhones];
                        next[index] = { ...next[index], label: event.target.value };
                        return { ...prev, secondaryPhones: next };
                      })
                    }
                  />
                </Form.Item>
                <Button
                  icon={<DeleteOutlined />}
                  type="text"
                  aria-label="Eliminar teléfono"
                  disabled={disabled}
                  onClick={() =>
                    onChange((prev) => ({
                      ...prev,
                      secondaryPhones: prev.secondaryPhones.filter((_, idx) => idx !== index),
                    }))
                  }
                />
              </Space>
              <Form.Item
                label="Número"
                required={false}
                help={phoneError ?? undefined}
                validateStatus={phoneError ? 'error' : undefined}
              >
                <PhoneInput
                  aria-label="Teléfono secundario"
                  defaultCountry={phone.country ?? defaultPhoneCountry}
                  international
                  value={phone.number || undefined}
                  disabled={disabled}
                  onChange={(value) =>
                    onChange((prev) => {
                      const next = [...prev.secondaryPhones];
                      next[index] = { ...next[index], number: (value ?? '') as E164Number | '' };
                      return { ...prev, secondaryPhones: next };
                    })
                  }
                  onCountryChange={(country) =>
                    onChange((prev) => {
                      const next = [...prev.secondaryPhones];
                      next[index] = { ...next[index], country: (country ?? defaultPhoneCountry) as CountryCode };
                      return { ...prev, secondaryPhones: next };
                    })
                  }
                  style={{ width: '100%' }}
                />
              </Form.Item>
            </Space>
          );
        })}
        <Button
          type="dashed"
          icon={<PlusOutlined />}
          onClick={() =>
            onChange((prev) => ({
              ...prev,
              secondaryPhones: [...prev.secondaryPhones, buildEmptySecondaryPhone(defaultPhoneCountry)],
            }))
          }
          disabled={disabled}
        >
          Agregar teléfono
        </Button>
      </Space>

      <Divider plain>Direcciones</Divider>
      <Space orientation="vertical" style={{ width: '100%' }} size="middle">
        {value.addresses.length === 0 ? (
          <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Sin direcciones guardadas" />
        ) : null}
        {value.addresses.map((address, index) => {
          const error = addressErrors[index] ?? null;
          return (
            <Space
              key={address.id ?? `address-${index}`}
              direction="vertical"
              style={{
                width: '100%',
                border: '1px solid var(--ant-color-border)',
                borderRadius: 8,
                padding: 12,
              }}
              size="small"
            >
              <Space style={{ width: '100%' }} align="start">
                <Form.Item label="Etiqueta" style={{ flex: 1 }} htmlFor={`customerAddressLabel-${index}`}>
                  <Input
                    id={`customerAddressLabel-${index}`}
                    placeholder="Ej: Casa"
                    value={address.label}
                    disabled={disabled}
                    maxLength={60}
                    onChange={(event) =>
                      onChange((prev) => {
                        const next = [...prev.addresses];
                        next[index] = { ...next[index], label: event.target.value };
                        return { ...prev, addresses: next };
                      })
                    }
                  />
                </Form.Item>
                <Button
                  icon={<DeleteOutlined />}
                  type="text"
                  aria-label="Eliminar dirección"
                  disabled={disabled}
                  onClick={() =>
                    onChange((prev) => ({
                      ...prev,
                      addresses: prev.addresses.filter((_, idx) => idx !== index),
                    }))
                  }
                />
              </Space>
              <Form.Item
                label="Calle y número"
                required={false}
                help={error?.street ?? undefined}
                validateStatus={error?.street ? 'error' : undefined}
              >
                <Input
                  placeholder="Av. Siempre Viva 742"
                  value={address.street}
                  disabled={disabled}
                  maxLength={128}
                  onChange={(event) =>
                    onChange((prev) => {
                      const next = [...prev.addresses];
                      next[index] = { ...next[index], street: event.target.value };
                      return { ...prev, addresses: next };
                    })
                  }
                />
              </Form.Item>
              <Form.Item label="Ciudad" help={error?.city ?? undefined} validateStatus={error?.city ? 'error' : undefined}>
                <Input
                  placeholder="Ciudad"
                  value={address.city}
                  disabled={disabled}
                  maxLength={60}
                  onChange={(event) =>
                    onChange((prev) => {
                      const next = [...prev.addresses];
                      next[index] = { ...next[index], city: event.target.value };
                      return { ...prev, addresses: next };
                    })
                  }
                />
              </Form.Item>
              <Row gutter={[12, 0]}>
                <Col xs={24} md={10}>
                  <Form.Item label="Provincia / Estado">
                    <Input
                      placeholder="Provincia / Estado"
                      value={address.state}
                      disabled={disabled}
                      maxLength={60}
                      onChange={(event) =>
                        onChange((prev) => {
                          const next = [...prev.addresses];
                          next[index] = { ...next[index], state: event.target.value };
                          return { ...prev, addresses: next };
                        })
                      }
                    />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={12} md={7}>
                  <Form.Item label="Código postal">
                    <Input
                      placeholder="Código postal"
                      value={address.postcode}
                      disabled={disabled}
                      maxLength={16}
                      onChange={(event) =>
                        onChange((prev) => {
                          const next = [...prev.addresses];
                          next[index] = { ...next[index], postcode: event.target.value };
                          return { ...prev, addresses: next };
                        })
                      }
                    />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={12} md={7}>
                  <Form.Item label="País">
                    <Input
                      placeholder="País"
                      value={address.country}
                      disabled={disabled}
                      maxLength={60}
                      onChange={(event) =>
                        onChange((prev) => {
                          const next = [...prev.addresses];
                          next[index] = { ...next[index], country: event.target.value };
                          return { ...prev, addresses: next };
                        })
                      }
                    />
                  </Form.Item>
                </Col>
              </Row>
              <Form.Item label="Teléfono de contacto">
                <PhoneInput
                  aria-label={`Teléfono de la dirección ${index + 1}`}
                  defaultCountry={(address.country || defaultPhoneCountry) as CountryCode}
                  international
                  value={(address.phoneNumber || undefined) as E164Number | undefined}
                  disabled={disabled}
                  onChange={(phoneNumber) =>
                    onChange((prev) => {
                      const next = [...prev.addresses];
                      next[index] = { ...next[index], phoneNumber: phoneNumber ?? '' };
                      return { ...prev, addresses: next };
                    })
                  }
                  style={{ width: '100%' }}
                />
              </Form.Item>
              <Form.Item label="Notas">
                <TextArea
                  placeholder="Notas"
                  value={address.notes}
                  disabled={disabled}
                  autoSize={{ minRows: 2, maxRows: 4 }}
                  maxLength={255}
                  onChange={(event) =>
                    onChange((prev) => {
                      const next = [...prev.addresses];
                      next[index] = { ...next[index], notes: event.target.value };
                      return { ...prev, addresses: next };
                    })
                  }
                />
              </Form.Item>
            </Space>
          );
        })}
        <Button
          type="dashed"
          icon={<PlusOutlined />}
          onClick={() =>
            onChange((prev) => ({
              ...prev,
              addresses: [...prev.addresses, buildEmptyAddress(defaultPhoneCountry)],
            }))
          }
          disabled={disabled}
        >
          Agregar dirección
        </Button>
      </Space>

      <Divider plain>Redes y notas</Divider>
      <Space orientation="vertical" style={{ width: '100%' }}>
        <Form.Item label="Foto">
          <ImageManager
            multiple={false}
            selectedSize="sm"
            value={buildAvatarSelection(value.avatarUrl)}
            onChange={(images) => {
              const selectedImage = images[0];
              const nextAvatarUrl =
                selectedImage?.file_path_large?.trim() ||
                selectedImage?.thumbnails?.default?.trim() ||
                '';

              onChange((prev) => ({ ...prev, avatarUrl: nextAvatarUrl }));
            }}
            options={{ defaultFolderName: 'Clientes' }}
          />
        </Form.Item>
        <Space orientation="vertical" style={{ width: '100%' }}>
          <Typography.Text type="secondary">Redes sociales</Typography.Text>
          <Form.Item label="Instagram" help={socialErrors.instagram ?? undefined} validateStatus={socialErrors.instagram ? 'error' : undefined}>
            <Input
              placeholder="https://instagram.com/miempresa"
              value={value.social.instagram}
              disabled={disabled}
              maxLength={200}
              onChange={(event) =>
                onChange((prev) => ({
                  ...prev,
                  social: { ...prev.social, instagram: event.target.value },
                }))
              }
            />
          </Form.Item>
          <Form.Item label="Facebook" help={socialErrors.facebook ?? undefined} validateStatus={socialErrors.facebook ? 'error' : undefined}>
            <Input
              placeholder="https://facebook.com/miempresa"
              value={value.social.facebook}
              disabled={disabled}
              maxLength={200}
              onChange={(event) =>
                onChange((prev) => ({
                  ...prev,
                  social: { ...prev.social, facebook: event.target.value },
                }))
              }
            />
          </Form.Item>
          <Form.Item label="TikTok" help={socialErrors.tiktok ?? undefined} validateStatus={socialErrors.tiktok ? 'error' : undefined}>
            <Input
              placeholder="https://tiktok.com/@miempresa"
              value={value.social.tiktok}
              disabled={disabled}
              maxLength={200}
              onChange={(event) =>
                onChange((prev) => ({
                  ...prev,
                  social: { ...prev.social, tiktok: event.target.value },
                }))
              }
            />
          </Form.Item>
          <Form.Item label="YouTube" help={socialErrors.youtube ?? undefined} validateStatus={socialErrors.youtube ? 'error' : undefined}>
            <Input
              placeholder="https://youtube.com/@miempresa"
              value={value.social.youtube}
              disabled={disabled}
              maxLength={200}
              onChange={(event) =>
                onChange((prev) => ({
                  ...prev,
                  social: { ...prev.social, youtube: event.target.value },
                }))
              }
            />
          </Form.Item>
          <Form.Item label="WhatsApp" help={socialErrors.whatsapp ?? undefined} validateStatus={socialErrors.whatsapp ? 'error' : undefined}>
            <Input
              placeholder="https://wa.me/54911..."
              value={value.social.whatsapp}
              disabled={disabled}
              maxLength={200}
              onChange={(event) =>
                onChange((prev) => ({
                  ...prev,
                  social: { ...prev.social, whatsapp: event.target.value },
                }))
              }
            />
          </Form.Item>
          <Form.Item label="Sitio web" help={socialErrors.website ?? undefined} validateStatus={socialErrors.website ? 'error' : undefined}>
            <Input
              placeholder="https://miempresa.com"
              value={value.social.website}
              disabled={disabled}
              maxLength={200}
              onChange={(event) =>
                onChange((prev) => ({
                  ...prev,
                  social: { ...prev.social, website: event.target.value },
                }))
              }
            />
          </Form.Item>
        </Space>
        <Form.Item label="Notas" htmlFor="customerNotes">
          <TextArea
            id="customerNotes"
            placeholder="Notas internas del cliente"
            value={value.notes}
            disabled={disabled}
            maxLength={500}
            autoSize={{ minRows: 3, maxRows: 6 }}
            onChange={(event) =>
              onChange((prev) => ({ ...prev, notes: event.target.value }))
            }
          />
        </Form.Item>
      </Space>
    </Space>
  );
}

export const validateSecondaryPhoneDraft = (phone: CustomerSecondaryPhoneDraft): string | null => {
  if (!phone.number) {
    return null;
  }
  const candidate = String(phone.number).trim();
  if (!candidate) {
    return null;
  }
  if (!isValidPhoneNumber(candidate)) {
    return 'Ingresá un teléfono válido (E.164)';
  }
  return null;
};
